/**
 * Buzerlístok — vlastná sada ikon (minimalistický line štýl, Lucide konvencie).
 * 24x24 viewBox, stroke = currentColor, round cap/join. Farbu zdedia z rodiča.
 *
 * Použitie:
 *   import { BuzerIcon } from "./BuzerIcons";
 *   <BuzerIcon name="sun" size={28} />
 *
 * Picker podľa kategórie:
 *   ICON_CATEGORIES["pohyb"]  // -> ["dumbbell","shoe",...]
 */

export type BuzerIconName = keyof typeof ICONS;

import type { CSSProperties } from "react";

type Props = {
  name: BuzerIconName | (string & {});  // v20 — also accept runtime-merged icon names (badge/level/nophone/rocket)
  size?: number;
  strokeWidth?: number;
  color?: string;
  className?: string;
  title?: string;
  style?: CSSProperties;
};

export const CATEGORY_LABELS: Record<string, string> = {
  "cas": "Čas a rutina",
  "pohyb": "Pohyb",
  "zdravie": "Zdravie",
  "mysel": "Myseľ a pohoda",
  "jedlo": "Jedlo a pitie",
  "domov": "Domácnosť",
  "praca": "Práca",
  "ucenie": "Učenie a kreativita",
  "socialne": "Sociálne",
  "priroda": "Príroda"
  // note: "ui" + "nalada" are intentionally NOT here, so UI-chrome + mood faces
  // stay out of the habit/category icon picker (but remain usable by name).
};

const ICONS: Record<string, { label: string; cat: string; svg: string }> = {
  // Mood faces (cat "nalada" — kept out of CATEGORY_LABELS so they don't show in pickers).
  "great":   { "label": "Skvelý",    "cat": "nalada", "svg": "<circle cx=\"8.5\" cy=\"10\" r=\"1\"/><circle cx=\"15.5\" cy=\"10\" r=\"1\"/><path d=\"M7.5 14 q4.5 5 9 0\"/>" },
  "good":    { "label": "Dobrý",     "cat": "nalada", "svg": "<circle cx=\"8.5\" cy=\"10\" r=\"1\"/><circle cx=\"15.5\" cy=\"10\" r=\"1\"/><path d=\"M8 14.5 q4 3 8 0\"/>" },
  "neutral": { "label": "Neutrálne", "cat": "nalada", "svg": "<circle cx=\"8.5\" cy=\"10\" r=\"1\"/><circle cx=\"15.5\" cy=\"10\" r=\"1\"/><line x1=\"8.5\" y1=\"15\" x2=\"15.5\" y2=\"15\"/>" },
  "wavy":    { "label": "Výkyvy",    "cat": "nalada", "svg": "<circle cx=\"8.5\" cy=\"10\" r=\"1\"/><circle cx=\"15.5\" cy=\"10\" r=\"1\"/><path d=\"M8 15 q1.75 -2 3.5 0 t3.5 0\"/>" },
  "hard":    { "label": "Ťažký",     "cat": "nalada", "svg": "<circle cx=\"8.5\" cy=\"10\" r=\"1\"/><circle cx=\"15.5\" cy=\"10\" r=\"1\"/><path d=\"M7.5 16 q4.5 -5 9 0\"/>" },
  // v9 — new glyphs (stretch/shower/pin/pray/run/walk/water) + the AI mark, from BuzerIcons (3).tsx
  "medal": {
    "label": "Medaila",
    "cat": "socialne",
    "svg": "<path d=\"M8.5 3 H15.5\"/><path d=\"M9 3 L11.4 10\"/><path d=\"M15 3 L12.6 10\"/><circle cx=\"12\" cy=\"15.5\" r=\"5.5\"/><path d=\"M11.1 14.4 L12.4 13.5 V18\"/>"
  },
  "medal2": {
    "label": "Medaila 2",
    "cat": "socialne",
    "svg": "<path d=\"M8.5 3 H15.5\"/><path d=\"M9 3 L11.4 10\"/><path d=\"M15 3 L12.6 10\"/><circle cx=\"12\" cy=\"15.5\" r=\"5.5\"/><path d=\"M10.6 14.3 C10.6 13 13.4 13 13.4 14.6 C13.4 15.6 12 16.4 10.5 18 H13.6\"/>"
  },
  "medal3": {
    "label": "Medaila 3",
    "cat": "socialne",
    "svg": "<path d=\"M8.5 3 H15.5\"/><path d=\"M9 3 L11.4 10\"/><path d=\"M15 3 L12.6 10\"/><circle cx=\"12\" cy=\"15.5\" r=\"5.5\"/><path d=\"M10.7 13.9 C11.5 13.1 13.5 13.3 13.3 14.8 C13.2 15.4 12.6 15.7 11.9 15.7 C12.6 15.7 13.4 16 13.4 17 C13.4 18.6 11.4 18.6 10.6 17.7\"/>"
  },
  "grid": {
    "label": "Tabuľka",
    "cat": "ui",
    "svg": "<rect x=\"3.5\" y=\"3.5\" width=\"7\" height=\"7\" rx=\"1.5\"/><rect x=\"13.5\" y=\"3.5\" width=\"7\" height=\"7\" rx=\"1.5\"/><rect x=\"3.5\" y=\"13.5\" width=\"7\" height=\"7\" rx=\"1.5\"/><rect x=\"13.5\" y=\"13.5\" width=\"7\" height=\"7\" rx=\"1.5\"/>"
  },
  "list": {
    "label": "Zoznam",
    "cat": "ui",
    "svg": "<circle cx=\"4.5\" cy=\"6\" r=\"1\"/><line x1=\"9\" y1=\"6\" x2=\"20\" y2=\"6\"/><circle cx=\"4.5\" cy=\"12\" r=\"1\"/><line x1=\"9\" y1=\"12\" x2=\"20\" y2=\"12\"/><circle cx=\"4.5\" cy=\"18\" r=\"1\"/><line x1=\"9\" y1=\"18\" x2=\"20\" y2=\"18\"/>"
  },
  "ai": {
    "label": "AI",
    "cat": "ui",
    "svg": "<path d=\"M10.3 6.8 C10.3 10.54 11.76 12 15.5 12 C11.76 12 10.3 13.46 10.3 17.2 C10.3 13.46 8.84 12 5.1000000000000005 12 C8.84 12 10.3 10.54 10.3 6.8 Z\"/><path d=\"M17.2 4.0 C17.2 5.73 17.87 6.4 19.599999999999998 6.4 C17.87 6.4 17.2 7.07 17.2 8.8 C17.2 7.07 16.53 6.4 14.799999999999999 6.4 C16.53 6.4 17.2 5.73 17.2 4.0 Z\"/>"
  },
  "pin": {
    "label": "Pripnúť",
    "cat": "ui",
    "svg": "<rect x=\"9\" y=\"3.5\" width=\"6\" height=\"2.5\" rx=\"1\"/><path d=\"M10 6 l-1.5 4 a1 1 0 0 0 0.9 1.5 h5.2 a1 1 0 0 0 0.9 -1.5 L14 6\"/><line x1=\"12\" y1=\"11.5\" x2=\"12\" y2=\"20\"/>"
  },
  "eye": {
    "label": "Zobraziť",
    "cat": "ui",
    "svg": "<path d=\"M2 12 C2 12 5 5 12 5 C19 5 22 12 22 12 C22 12 19 19 12 19 C5 19 2 12 2 12Z\"/><circle cx=\"12\" cy=\"12\" r=\"3\"/>"
  },
  "eyeoff": {
    "label": "Skryť",
    "cat": "ui",
    "svg": "<path d=\"M2 12 C2 12 5 5 12 5 C19 5 22 12 22 12 C22 12 19 19 12 19 C5 19 2 12 2 12Z\"/><circle cx=\"12\" cy=\"12\" r=\"3\"/><line x1=\"4\" y1=\"4\" x2=\"20\" y2=\"20\"/>"
  },
  "pray": {
    "label": "Vďaka",
    "cat": "mysel",
    "svg": "<path d=\"M12 4.5 C11 4.5 10 6 9.5 8 C8.8 10.5 8 13 8.2 15 C8.3 16.6 9 17.5 10.5 17.5 L12 17.5 Z\"/><path d=\"M12 4.5 C13 4.5 14 6 14.5 8 C15.2 10.5 16 13 15.8 15 C15.7 16.6 15 17.5 13.5 17.5 L12 17.5 Z\"/><path d=\"M9.7 17.5 L8.7 20.5 H15.3 L14.3 17.5\"/>"
  },
  "run": {
    "label": "Beh",
    "cat": "pohyb",
    "svg": "<path d=\"M6 14.6 C5.4 11 6.8 9 10 9.2 C13.4 9.4 16.6 10 19.6 11.4 C21.4 12.2 22.4 13 22 14.2 C21.6 15 19.8 15.1 17.4 15.1 L6.8 14.9 C6.2 14.9 6.1 14.8 6 14.6 Z\"/><path d=\"M6.4 15.8 H17.4 Q20.6 15.8 22 13.4\"/><path d=\"M11.6 12.6 l1 -1.4\"/><path d=\"M13.1 12.7 l1 -1.4\"/><path d=\"M14.6 12.9 l1 -1.4\"/><path d=\"M16.1 13.2 l0.9 -1.4\"/><line x1=\"1\" y1=\"11.8\" x2=\"4.8\" y2=\"11.8\"/><line x1=\"0.6\" y1=\"14.4\" x2=\"4.4\" y2=\"14.4\"/><line x1=\"2\" y1=\"17\" x2=\"5\" y2=\"17\"/>"
  },
  "shower": {
    "label": "Sprcha",
    "cat": "zdravie",
    "svg": "<path d=\"M5 9 a7 4 0 0 1 14 0\"/><line x1=\"5\" y1=\"9\" x2=\"19\" y2=\"9\"/><path d=\"M12 5.5 V3 h5\"/><line x1=\"8\" y1=\"11.5\" x2=\"8\" y2=\"13.5\"/><line x1=\"10.5\" y1=\"11.5\" x2=\"10.5\" y2=\"14\"/><line x1=\"13.5\" y1=\"11.5\" x2=\"13.5\" y2=\"14\"/><line x1=\"16\" y1=\"11.5\" x2=\"16\" y2=\"13.5\"/><line x1=\"9\" y1=\"15.5\" x2=\"9\" y2=\"17\"/><line x1=\"12\" y1=\"16\" x2=\"12\" y2=\"17.5\"/><line x1=\"15\" y1=\"15.5\" x2=\"15\" y2=\"17\"/>"
  },
  "stretch": {
    "label": "Strečing",
    "cat": "pohyb",
    "svg": "<circle cx=\"12\" cy=\"4.5\" r=\"2\"/><path d=\"M12 6.5 V13\"/><path d=\"M12 8 L8.3 4.6\"/><path d=\"M12 8 L15.7 4.6\"/><path d=\"M12 13 L9 20\"/><path d=\"M12 13 L15 20\"/>"
  },
  "walk": {
    "label": "Chôdza",
    "cat": "pohyb",
    "svg": "<circle cx=\"12\" cy=\"5\" r=\"2\"/><path d=\"M12 7 L11 13\"/><path d=\"M11 13 L13 16.5 L13 20\"/><path d=\"M11 13 L9 16.5 L9 20\"/><path d=\"M12 8.5 L14 11.5\"/><path d=\"M12 8.5 L10 11.5\"/>"
  },
  "water": {
    "label": "Voda",
    "cat": "jedlo",
    "svg": "<rect x=\"10\" y=\"3\" width=\"4\" height=\"2.5\" rx=\"0.5\"/><path d=\"M9 7 h6 a1 1 0 0 1 1 1 v10 a2 2 0 0 1 -2 2 h-4 a2 2 0 0 1 -2 -2 V8 a1 1 0 0 1 1 -1Z\"/><path d=\"M10.5 5.5 h3 V7\"/><line x1=\"9\" y1=\"12\" x2=\"11\" y2=\"12\"/><line x1=\"9\" y1=\"15\" x2=\"11\" y2=\"15\"/>"
  },
  "sunrise": {
    "label": "Východ slnka",
    "cat": "cas",
    "svg": "<line x1=\"3\" y1=\"20\" x2=\"21\" y2=\"20\"/><path d=\"M7.5 16 a4.5 4.5 0 0 1 9 0\"/><line x1=\"12\" y1=\"3\" x2=\"12\" y2=\"6\"/><line x1=\"5.5\" y1=\"9.5\" x2=\"7\" y2=\"11\"/><line x1=\"18.5\" y1=\"9.5\" x2=\"17\" y2=\"11\"/><line x1=\"2\" y1=\"16\" x2=\"4.5\" y2=\"16\"/><line x1=\"19.5\" y1=\"16\" x2=\"22\" y2=\"16\"/>"
  },
  "sun": {
    "label": "Slnko",
    "cat": "cas",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"4\"/><line x1=\"12\" y1=\"2\" x2=\"12\" y2=\"4\"/><line x1=\"12\" y1=\"20\" x2=\"12\" y2=\"22\"/><line x1=\"2\" y1=\"12\" x2=\"4\" y2=\"12\"/><line x1=\"20\" y1=\"12\" x2=\"22\" y2=\"12\"/><line x1=\"4.9\" y1=\"4.9\" x2=\"6.3\" y2=\"6.3\"/><line x1=\"17.7\" y1=\"17.7\" x2=\"19.1\" y2=\"19.1\"/><line x1=\"4.9\" y1=\"19.1\" x2=\"6.3\" y2=\"17.7\"/><line x1=\"17.7\" y1=\"6.3\" x2=\"19.1\" y2=\"4.9\"/>"
  },
  "sunset": {
    "label": "Západ slnka",
    "cat": "cas",
    "svg": "<line x1=\"3\" y1=\"20\" x2=\"21\" y2=\"20\"/><path d=\"M7.5 16 a4.5 4.5 0 0 1 9 0\"/><line x1=\"2\" y1=\"16\" x2=\"4.5\" y2=\"16\"/><line x1=\"19.5\" y1=\"16\" x2=\"22\" y2=\"16\"/><polyline points=\"9.5,5 12,7.5 14.5,5\"/>"
  },
  "moon": {
    "label": "Mesiac",
    "cat": "cas",
    "svg": "<path d=\"M19 12.8 A7 7 0 1 1 11.2 5 A5.5 5.5 0 0 0 19 12.8 Z\"/>"
  },
  "night": {
    "label": "Noc",
    "cat": "cas",
    "svg": "<path d=\"M8 4.3 C8 6.60 8.90 7.5 11.2 7.5 C8.90 7.5 8 8.40 8 10.7 C8 8.40 7.10 7.5 4.8 7.5 C7.10 7.5 8 6.60 8 4.3 Z\"/><path d=\"M16.5 11 C16.5 12.44 17.06 13 18.5 13 C17.06 13 16.5 13.56 16.5 15 C16.5 13.56 15.94 13 14.5 13 C15.94 13 16.5 12.44 16.5 11 Z\"/>"
  },
  "alarm": {
    "label": "Budík",
    "cat": "cas",
    "svg": "<circle cx=\"12\" cy=\"13.5\" r=\"5.6\"/><path d=\"M3.6 8.8 A3 3 0 0 1 8.4 5.9\"/><path d=\"M3.9 8.4 8.1 6.1\"/><path d=\"M20.4 8.8 A3 3 0 0 0 15.6 5.9\"/><path d=\"M20.1 8.4 15.9 6.1\"/><path d=\"M10.9 5.2 13.1 5.2\"/><polyline points=\"9.7 11.5 12 13.5 12 9.5\"/><path d=\"M8 17.6 6.2 19.8\"/><path d=\"M16 17.6 17.8 19.8\"/>"
  },
  "clock": {
    "label": "Hodiny",
    "cat": "cas",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><polyline points=\"12,7 12,12 16,14\"/>"
  },
  "hourglass": {
    "label": "Presýpacie hodiny",
    "cat": "cas",
    "svg": "<path d=\"M6 4 h12\"/><path d=\"M6 20 h12\"/><path d=\"M7 4 c0 5 10 5 10 16\"/><path d=\"M17 4 c0 5 -10 5 -10 16\"/>"
  },
  "bed": {
    "label": "Posteľ / spánok",
    "cat": "cas",
    "svg": "<path d=\"M3 18 v-4 a2 2 0 0 1 2 -2 h14 a2 2 0 0 1 2 2 v4\"/><line x1=\"3\" y1=\"18\" x2=\"3\" y2=\"20\"/><line x1=\"21\" y1=\"18\" x2=\"21\" y2=\"20\"/><path d=\"M6 12 v-2 a1.5 1.5 0 0 1 1.5 -1.5 h9 a1.5 1.5 0 0 1 1.5 1.5 v2\"/>"
  },
  "calendar": {
    "label": "Kalendár",
    "cat": "cas",
    "svg": "<rect x=\"4\" y=\"5\" width=\"16\" height=\"15\" rx=\"2\"/><line x1=\"4\" y1=\"9\" x2=\"20\" y2=\"9\"/><line x1=\"8\" y1=\"3\" x2=\"8\" y2=\"6\"/><line x1=\"16\" y1=\"3\" x2=\"16\" y2=\"6\"/>"
  },
  "dumbbell": {
    "label": "Činka",
    "cat": "pohyb",
    "svg": "<line x1=\"9.5\" y1=\"12\" x2=\"14.5\" y2=\"12\"/><line x1=\"6.5\" y1=\"8\" x2=\"6.5\" y2=\"16\"/><line x1=\"9\" y1=\"9.5\" x2=\"9\" y2=\"14.5\"/><line x1=\"17.5\" y1=\"8\" x2=\"17.5\" y2=\"16\"/><line x1=\"15\" y1=\"9.5\" x2=\"15\" y2=\"14.5\"/><line x1=\"4\" y1=\"10.5\" x2=\"4\" y2=\"13.5\"/><line x1=\"20\" y1=\"10.5\" x2=\"20\" y2=\"13.5\"/>"
  },
  "shoe": {
    "label": "Beh / tenisky",
    "cat": "pohyb",
    "svg": "<path d=\"M3 17 v-3 l5 -1.5 3.5 -3 c0.5 2 2 3 4 3 H20 a2 2 0 0 1 2 2 v2.5 a1 1 0 0 1 -1 1 H4 a1 1 0 0 1 -1 -1Z\"/><line x1=\"3\" y1=\"18\" x2=\"21\" y2=\"18\"/><line x1=\"10\" y1=\"11\" x2=\"11.5\" y2=\"13\"/><line x1=\"12.5\" y1=\"9.5\" x2=\"14\" y2=\"11.5\"/>"
  },
  "bike": {
    "label": "Bicykel",
    "cat": "pohyb",
    "svg": "<circle cx=\"6\" cy=\"16\" r=\"3.5\"/><circle cx=\"18\" cy=\"16\" r=\"3.5\"/><path d=\"M6 16 l4 -6 h5\"/><path d=\"M10 10 l3 6\"/><line x1=\"9\" y1=\"8\" x2=\"11.5\" y2=\"8\"/>"
  },
  "swim": {
    "label": "Plávanie",
    "cat": "pohyb",
    "svg": "<path d=\"M3 14 q2 -2 4 0 t4 0 4 0 4 0\"/><path d=\"M3 18 q2 -2 4 0 t4 0 4 0 4 0\"/><circle cx=\"9\" cy=\"7\" r=\"1.6\"/><path d=\"M11 9 l4 -1 3 2\"/>"
  },
  "pulse": {
    "label": "Aktivita / tep",
    "cat": "pohyb",
    "svg": "<polyline points=\"3,12 8,12 10,7 13,17 15,12 21,12\"/>"
  },
  "footprints": {
    "label": "Kroky",
    "cat": "pohyb",
    "svg": "<path d=\"M6 5 c2 0 2 4 1 6 c-2 1 -3 -1 -3 -3 c0 -2 0 -3 2 -3Z\"/><path d=\"M4 13 c1.5 0 1.5 2 1 3 H4 c-0.5 -1 -0.5 -3 0 -3Z\"/><path d=\"M16 9 c2 0 2 4 1 6 c-2 1 -3 -1 -3 -3 c0 -2 0 -3 2 -3Z\"/><path d=\"M14 17 c1.5 0 1.5 2 1 3 h-2 c-0.5 -1 -0.5 -3 1 -3Z\"/>"
  },
  "mountain": {
    "label": "Turistika",
    "cat": "pohyb",
    "svg": "<path d=\"M3 19 l6 -10 4 6 2 -3 6 7Z\"/><path d=\"M9 9 l1.5 2.4\"/>"
  },
  "ball": {
    "label": "Lopta",
    "cat": "pohyb",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><path d=\"M12 4 l2.5 4 -2.5 4 -2.5 -4Z\"/><path d=\"M19 9 l-4.5 3\"/><path d=\"M5 9 l4.5 3\"/><path d=\"M8 19 l1.5 -5\"/><path d=\"M16 19 l-1.5 -5\"/>"
  },
  "stopwatch": {
    "label": "Stopky",
    "cat": "pohyb",
    "svg": "<circle cx=\"12\" cy=\"13\" r=\"7\"/><line x1=\"12\" y1=\"13\" x2=\"15\" y2=\"11\"/><line x1=\"10\" y1=\"3\" x2=\"14\" y2=\"3\"/><line x1=\"12\" y1=\"3\" x2=\"12\" y2=\"6\"/>"
  },
  "flame": {
    "label": "Energia / rozcvička",
    "cat": "pohyb",
    "svg": "<path d=\"M11.9 3.8 C11.7 3.9 10.9 4.0 10.5 4.5 C10.1 5.0 9.7 6.0 9.6 6.6 C9.5 7.2 9.5 7.6 9.7 8.3 C9.9 9.0 10.6 10.2 10.8 11.0 C11.0 11.8 10.8 12.7 10.6 13.2 C10.4 13.7 10.2 13.8 9.9 14.0 C9.6 14.2 9.1 14.4 8.7 14.4 C8.3 14.4 8.0 14.4 7.5 14.2 C7.0 14.0 6.2 13.5 5.8 13.4 C5.4 13.3 5.3 13.3 5.2 13.7 C5.1 14.1 4.9 14.9 5.0 15.6 C5.1 16.3 5.3 17.2 5.6 17.9 C5.9 18.5 6.1 19.0 6.6 19.5 C7.1 20.0 7.8 20.6 8.4 21.0 C9.0 21.4 9.6 21.7 10.3 21.8 C11.0 22.0 11.6 21.9 12.3 21.9 C13.0 21.9 13.7 21.8 14.3 21.6 C14.9 21.4 15.6 21.0 16.1 20.6 C16.6 20.2 16.9 20.0 17.3 19.5 C17.7 19.0 18.1 18.5 18.4 17.7 C18.7 16.9 18.9 15.7 18.9 14.8 C18.9 13.9 18.7 13.1 18.4 12.4 C18.1 11.7 18.0 11.6 17.3 10.8 C16.6 10.0 15.1 9.0 14.2 7.8 C13.3 6.6 12.3 4.6 11.9 3.9 C11.5 3.2 12.1 3.7 11.9 3.8 Z\"/>"
  },
  "heart": {
    "label": "Srdce",
    "cat": "zdravie",
    "svg": "<path d=\"M12 20 C5 15 3 11 3 8 a4 4 0 0 1 8 -1 a4 4 0 0 1 8 1 c0 3 -2 7 -7 12Z\" transform=\"translate(0,-1)\"/>"
  },
  "pill": {
    "label": "Tabletka",
    "cat": "zdravie",
    "svg": "<rect x=\"4\" y=\"9\" width=\"16\" height=\"6\" rx=\"3\" transform=\"rotate(-30 12 12)\"/><line x1=\"12\" y1=\"6.5\" x2=\"9\" y2=\"17.5\"/>"
  },
  "drop": {
    "label": "Hydratácia",
    "cat": "zdravie",
    "svg": "<path d=\"M12 3 C8 8 6 11 6 14 a6 6 0 0 0 12 0 c0 -3 -2 -6 -6 -11Z\"/>"
  },
  "apple": {
    "label": "Jablko",
    "cat": "zdravie",
    "svg": "<path d=\"M12 8 c-2 -2 -6 -1 -6 4 c0 4 3 8 6 8 c3 0 6 -4 6 -8 c0 -5 -4 -6 -6 -4Z\"/><path d=\"M12 8 c0 -2 1 -3 3 -3.5\"/>"
  },
  "tooth": {
    "label": "Zuby",
    "cat": "zdravie",
    "svg": "<path d=\"M7 4 c2 -1 3 1 5 1 c2 0 3 -2 5 -1 c2 1 1 5 0.5 9 c-0.5 3 -1 7 -2.5 7 c-1.5 0 -1 -4 -3 -4 c-2 0 -1.5 4 -3 4 c-1.5 0 -2 -4 -2.5 -7 c-0.5 -4 -1.5 -8 0.5 -9Z\"/>"
  },
  "scale": {
    "label": "Váha",
    "cat": "zdravie",
    "svg": "<rect x=\"4\" y=\"4\" width=\"16\" height=\"16\" rx=\"3\"/><path d=\"M9 16 a3 3 0 0 1 6 0\"/><line x1=\"12\" y1=\"13\" x2=\"13.5\" y2=\"14.5\"/>"
  },
  "thermometer": {
    "label": "Teplota",
    "cat": "zdravie",
    "svg": "<path d=\"M12 4 a2 2 0 0 1 2 2 v8 a3.5 3.5 0 1 1 -4 0 V6 a2 2 0 0 1 2 -2Z\"/><circle cx=\"12\" cy=\"17\" r=\"1.4\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "cross": {
    "label": "Lekár / pomoc",
    "cat": "zdravie",
    "svg": "<rect x=\"4\" y=\"4\" width=\"16\" height=\"16\" rx=\"4\"/><line x1=\"12\" y1=\"8\" x2=\"12\" y2=\"16\"/><line x1=\"8\" y1=\"12\" x2=\"16\" y2=\"12\"/>"
  },
  "bandage": {
    "label": "Náplasť",
    "cat": "zdravie",
    "svg": "<rect x=\"3\" y=\"8\" width=\"18\" height=\"8\" rx=\"4\" transform=\"rotate(-30 12 12)\"/><line x1=\"10.3\" y1=\"11\" x2=\"12.3\" y2=\"14.5\"/><line x1=\"11.7\" y1=\"9.5\" x2=\"13.7\" y2=\"13\"/>"
  },
  "shield": {
    "label": "Imunita / prevencia",
    "cat": "zdravie",
    "svg": "<path d=\"M12 3 l7 3 v5 c0 5 -3 8 -7 10 c-4 -2 -7 -5 -7 -10 V6Z\"/><polyline points=\"9.5,12 11.5,14 15,9.5\"/>"
  },
  "brain": {
    "label": "Mozog",
    "cat": "mysel",
    "svg": "<path d=\"M12 5.5 a3 3 0 0 0 -4.6 2.4 a2.6 2.6 0 0 0 -1 4.8 a2.7 2.7 0 0 0 1 3.8 a2.6 2.6 0 0 0 4.6 -1.5 Z\"/><path d=\"M12 5.5 a3 3 0 0 1 4.6 2.4 a2.6 2.6 0 0 1 1 4.8 a2.7 2.7 0 0 1 -1 3.8 a2.6 2.6 0 0 1 -4.6 -1.5 Z\"/><path d=\"M9.3 9 a1.6 1.6 0 0 0 0 3\"/><path d=\"M14.7 9 a1.6 1.6 0 0 1 0 3\"/>"
  },
  "smile": {
    "label": "Nálada",
    "cat": "mysel",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><path d=\"M8.5 14 q3.5 2.5 7 0\"/><circle cx=\"9.2\" cy=\"10\" r=\"0.6\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"14.8\" cy=\"10\" r=\"0.6\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "laugh": {
    "label": "Smiech",
    "cat": "mysel",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><path d=\"M7.4 13.2 q4.6 4.2 9.2 0Z\"/><path d=\"M8 10.2 q1.2 -1.5 2.4 0\"/><path d=\"M13.6 10.2 q1.2 -1.5 2.4 0\"/>"
  },
  "wow": {
    "label": "Prekvapenie",
    "cat": "mysel",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><ellipse cx=\"12\" cy=\"14.4\" rx=\"1.7\" ry=\"2.1\"/><circle cx=\"9.2\" cy=\"9.8\" r=\"0.9\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"14.8\" cy=\"9.8\" r=\"0.9\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "cry": {
    "label": "Plač",
    "cat": "mysel",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><path d=\"M8.6 15.6 q3.4 -2.6 6.8 0\"/><circle cx=\"9.2\" cy=\"10\" r=\"0.6\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"14.8\" cy=\"10\" r=\"0.6\" fill=\"currentColor\" stroke=\"none\"/><path d=\"M9.2 11.6 q-1 1.7 0 2.3 q1 -0.6 0 -2.3Z\"/>"
  },
  "meditation": {
    "label": "Meditácia",
    "cat": "mysel",
    "svg": "<circle cx=\"12\" cy=\"6.5\" r=\"2\"/><path d=\"M12 9 v4\"/><path d=\"M12 13 l-5 4 c2 1 8 1 10 0Z\"/><path d=\"M12 11 l4 2\"/><path d=\"M12 11 l-4 2\"/>"
  },
  "candle": {
    "label": "Sviečka",
    "cat": "mysel",
    "svg": "<rect x=\"9\" y=\"9\" width=\"6\" height=\"11\" rx=\"1\"/><line x1=\"12\" y1=\"5\" x2=\"12\" y2=\"9\"/><path d=\"M12 2 c1.5 1.5 1.5 3 0 4 c-1.5 -1 -1.5 -2.5 0 -4Z\"/>"
  },
  "bookopen": {
    "label": "Čítanie",
    "cat": "mysel",
    "svg": "<path d=\"M12 6 c-2 -1.5 -5 -1.5 -8 0 v12 c3 -1.5 6 -1.5 8 0Z\"/><path d=\"M12 6 c2 -1.5 5 -1.5 8 0 v12 c-3 -1.5 -6 -1.5 -8 0Z\"/>"
  },
  "music": {
    "label": "Hudba",
    "cat": "mysel",
    "svg": "<path d=\"M9 17 V6 l9 -2 v11\"/><circle cx=\"7\" cy=\"17\" r=\"2\"/><circle cx=\"16\" cy=\"15\" r=\"2\"/><line x1=\"9\" y1=\"8\" x2=\"18\" y2=\"6\"/>"
  },
  "headphones": {
    "label": "Slúchadlá",
    "cat": "mysel",
    "svg": "<path d=\"M5 14 v-2 a7 7 0 0 1 14 0 v2\"/><rect x=\"3.5\" y=\"13\" width=\"3.5\" height=\"6\" rx=\"1.5\"/><rect x=\"17\" y=\"13\" width=\"3.5\" height=\"6\" rx=\"1.5\"/>"
  },
  "sparkles": {
    "label": "Vďačnosť / jas",
    "cat": "mysel",
    "svg": "<path d=\"M10 5 C10 7.88 11.12 9 14 9 C11.12 9 10 10.12 10 13 C10 10.12 8.88 9 6 9 C8.88 9 10 7.88 10 5 Z\"/><path d=\"M17 12.6 C17 14.33 17.67 15 19.4 15 C17.67 15 17 15.67 17 17.4 C17 15.67 16.33 15 14.6 15 C16.33 15 17 14.33 17 12.6 Z\"/>"
  },
  "party": {
    "label": "Oslava",
    "cat": "ui",
    "svg": "<path d=\"M5.8 11.3 2 22l10.7-3.79\"/><path d=\"M4 3h.01\"/><path d=\"M22 8h.01\"/><path d=\"M15 2h.01\"/><path d=\"M22 20h.01\"/><path d=\"m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10\"/><path d=\"m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17\"/><path d=\"m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7\"/><path d=\"M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z\"/>"
  },
  "journal": {
    "label": "Denník",
    "cat": "mysel",
    "svg": "<rect x=\"5\" y=\"4\" width=\"13\" height=\"16\" rx=\"2\"/><line x1=\"9\" y1=\"4\" x2=\"9\" y2=\"20\"/><line x1=\"12\" y1=\"9\" x2=\"15\" y2=\"9\"/><line x1=\"12\" y1=\"12\" x2=\"15\" y2=\"12\"/>"
  },
  "feather": {
    "label": "Ľahkosť",
    "cat": "mysel",
    "svg": "<path d=\"M19 5 c-6 -1 -12 4 -13 11 l-1 3\"/><path d=\"M19 5 c1 6 -4 12 -11 13\"/><line x1=\"8\" y1=\"12\" x2=\"14\" y2=\"9\"/><line x1=\"7\" y1=\"16\" x2=\"12\" y2=\"13\"/>"
  },
  "mug": {
    "label": "Káva / čaj",
    "cat": "jedlo",
    "svg": "<path d=\"M5 8 h11 v6 a4 4 0 0 1 -4 4 H9 a4 4 0 0 1 -4 -4Z\"/><path d=\"M16 9 h2 a2 2 0 0 1 0 5 h-2\"/><line x1=\"8\" y1=\"3\" x2=\"8\" y2=\"5.5\"/><line x1=\"12\" y1=\"3\" x2=\"12\" y2=\"5.5\"/>"
  },
  "glass": {
    "label": "Pohár vody",
    "cat": "jedlo",
    "svg": "<path d=\"M7 5 h10 l-1 14 a1 1 0 0 1 -1 1 H9 a1 1 0 0 1 -1 -1Z\"/><path d=\"M7.4 10 h9.2\"/>"
  },
  "carrot": {
    "label": "Zelenina",
    "cat": "jedlo",
    "svg": "<path d=\"M8.5 9 Q12 7.5 15.5 9 L12 20.5 Z\"/><path d=\"M9.5 12 h1.4\"/><path d=\"M10.3 15 h1.4\"/><path d=\"M12 9 V4\"/><path d=\"M12 6.5 Q9.5 5 8 5.5\"/><path d=\"M12 6.5 Q14.5 5 16 5.5\"/>"
  },
  "bread": {
    "label": "Chlieb",
    "cat": "jedlo",
    "svg": "<path d=\"M5 12 a7 4 0 0 1 14 0 v6 a1 1 0 0 1 -1 1 H6 a1 1 0 0 1 -1 -1Z\"/><line x1=\"9\" y1=\"10\" x2=\"9\" y2=\"18\"/><line x1=\"13\" y1=\"10\" x2=\"13\" y2=\"18\"/>"
  },
  "fish": {
    "label": "Ryba",
    "cat": "jedlo",
    "svg": "<path d=\"M4 12 c4 -5 11 -5 15 0 c-4 5 -11 5 -15 0Z\"/><path d=\"M19 12 l3 -3 v6Z\"/><circle cx=\"8\" cy=\"11\" r=\"0.7\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "egg": {
    "label": "Vajce",
    "cat": "jedlo",
    "svg": "<path d=\"M12 4 c-3 0 -5 5 -5 9 a5 5 0 0 0 10 0 c0 -4 -2 -9 -5 -9Z\"/>"
  },
  "bowl": {
    "label": "Šalát / miska",
    "cat": "jedlo",
    "svg": "<path d=\"M3 11 h18 a9 9 0 0 1 -18 0Z\"/><path d=\"M8 11 c0 -3 8 -3 8 0\"/><line x1=\"12\" y1=\"8\" x2=\"12\" y2=\"11\"/>"
  },
  "bottle": {
    "label": "Fľaša",
    "cat": "jedlo",
    "svg": "<path d=\"M10 3 h4 v3 l1 2 v11 a1 1 0 0 1 -1 1 H10 a1 1 0 0 1 -1 -1 V8 l1 -2Z\"/><line x1=\"9\" y1=\"12\" x2=\"15\" y2=\"12\"/>"
  },
  "herb": {
    "label": "Bylinky",
    "cat": "jedlo",
    "svg": "<path d=\"M12 20 V9\"/><path d=\"M12 12 c-3 0 -5 -2 -5 -5 c3 0 5 2 5 5Z\"/><path d=\"M12 10 c3 0 5 -2 5 -5 c-3 0 -5 2 -5 5Z\"/>"
  },
  "cherries": {
    "label": "Ovocie",
    "cat": "jedlo",
    "svg": "<circle cx=\"8\" cy=\"16\" r=\"3\"/><circle cx=\"15\" cy=\"17\" r=\"3\"/><path d=\"M8 13 c1 -5 6 -7 9 -7\"/><path d=\"M15 14 c0 -4 1 -6 2 -8\"/>"
  },
  "house": {
    "label": "Domov",
    "cat": "domov",
    "svg": "<path d=\"M4 11 l8 -7 8 7\"/><path d=\"M6 10 v9 h12 v-9\"/><rect x=\"10\" y=\"14\" width=\"4\" height=\"5\"/>"
  },
  "broom": {
    "label": "Upratovanie",
    "cat": "domov",
    "svg": "<line x1=\"18\" y1=\"4\" x2=\"10\" y2=\"12\"/><path d=\"M10 12 l-5 5 c-1 1 0 3 1 4 l3 -3 4 -4Z\"/><line x1=\"7\" y1=\"15\" x2=\"9\" y2=\"17\"/><line x1=\"9\" y1=\"13\" x2=\"11\" y2=\"15\"/>"
  },
  "shirt": {
    "label": "Pranie / oblečenie",
    "cat": "domov",
    "svg": "<path d=\"M8 4 l-4 3 2 3 2 -1 v8 h8 v-8 l2 1 2 -3 -4 -3 -2 2 -2 0Z\"/>"
  },
  "plate": {
    "label": "Riad",
    "cat": "domov",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><circle cx=\"12\" cy=\"12\" r=\"4\"/>"
  },
  "trash": {
    "label": "Smeti",
    "cat": "domov",
    "svg": "<path d=\"M6 7 l1 13 h10 l1 -13\"/><line x1=\"4\" y1=\"7\" x2=\"20\" y2=\"7\"/><path d=\"M9 7 V5 h6 v2\"/><line x1=\"10\" y1=\"11\" x2=\"10\" y2=\"17\"/><line x1=\"14\" y1=\"11\" x2=\"14\" y2=\"17\"/>"
  },
  "plantpot": {
    "label": "Polievanie kvetov",
    "cat": "domov",
    "svg": "<path d=\"M7 12 h10 l-1.5 8 h-7Z\"/><path d=\"M12 12 c0 -4 -2 -6 -5 -6 c0 4 2 6 5 6Z\"/><path d=\"M12 12 c0 -3 2 -5 5 -5 c0 3 -2 5 -5 5Z\"/>"
  },
  "wrench": {
    "label": "Náradie",
    "cat": "domov",
    "svg": "<path d=\"M15 4 a4 4 0 0 0 -1 7 l-9 9 2 2 9 -9 a4 4 0 0 0 5 -5 l-3 3 -2 -2 3 -3 a4 4 0 0 0 -4 -2Z\"/>"
  },
  "recycle": {
    "label": "Recyklácia",
    "cat": "domov",
    "svg": "<path d=\"M8 6 l2 -3 2 3\"/><path d=\"M10 3 l-4 7 2 3\"/><path d=\"M18 11 l1 4 -4 0\"/><path d=\"M19 14 l-4 6 -4 0\"/><path d=\"M5 13 l-1 4 4 1\"/><path d=\"M5 17 l4 0 2 -3\"/>"
  },
  "key": {
    "label": "Kľúče",
    "cat": "domov",
    "svg": "<circle cx=\"8\" cy=\"8\" r=\"4\"/><line x1=\"11\" y1=\"11\" x2=\"20\" y2=\"20\"/><line x1=\"17\" y1=\"17\" x2=\"19\" y2=\"15\"/><line x1=\"15\" y1=\"15\" x2=\"17\" y2=\"13\"/>"
  },
  "bulb": {
    "label": "Svetlo / nápad",
    "cat": "domov",
    "svg": "<path d=\"M9 16 a6 6 0 1 1 6 0 v2 h-6Z\"/><line x1=\"10\" y1=\"20\" x2=\"14\" y2=\"20\"/>"
  },
  "info": {
    "label": "Vysvetlenie",
    "cat": "ui",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"9\"/><line x1=\"12\" y1=\"11\" x2=\"12\" y2=\"16\"/><circle cx=\"12\" cy=\"7.8\" r=\"0.6\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "laptop": {
    "label": "Počítač",
    "cat": "praca",
    "svg": "<rect x=\"5\" y=\"5\" width=\"14\" height=\"9\" rx=\"1\"/><path d=\"M3 18 h18 l-1 -2 H4Z\"/>"
  },
  "briefcase": {
    "label": "Práca",
    "cat": "praca",
    "svg": "<rect x=\"3\" y=\"8\" width=\"18\" height=\"11\" rx=\"2\"/><path d=\"M9 8 V6 a1 1 0 0 1 1 -1 h4 a1 1 0 0 1 1 1 v2\"/><line x1=\"3\" y1=\"13\" x2=\"21\" y2=\"13\"/>"
  },
  "clipboard": {
    "label": "Úlohy",
    "cat": "praca",
    "svg": "<rect x=\"5\" y=\"5\" width=\"14\" height=\"16\" rx=\"2\"/><rect x=\"9\" y=\"3\" width=\"6\" height=\"4\" rx=\"1\"/><polyline points=\"8.5,12 10,13.5 12.5,11\"/><line x1=\"14\" y1=\"12.5\" x2=\"16\" y2=\"12.5\"/><polyline points=\"8.5,16.5 10,18 12.5,15.5\"/><line x1=\"14\" y1=\"17\" x2=\"16\" y2=\"17\"/>"
  },
  "pen": {
    "label": "Písanie",
    "cat": "praca",
    "svg": "<path d=\"M16 4 l4 4 -11 11 -5 1 1 -5Z\"/><line x1=\"14\" y1=\"6\" x2=\"18\" y2=\"10\"/>"
  },
  "target": {
    "label": "Cieľ",
    "cat": "praca",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><circle cx=\"12\" cy=\"12\" r=\"4.5\"/><circle cx=\"12\" cy=\"12\" r=\"1.3\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "folder": {
    "label": "Priečinok",
    "cat": "praca",
    "svg": "<path d=\"M4 7 h5 l2 2 h9 a1 1 0 0 1 1 1 v8 a1 1 0 0 1 -1 1 H4 a1 1 0 0 1 -1 -1 V8 a1 1 0 0 1 1 -1Z\"/>"
  },
  "mail": {
    "label": "E-mail",
    "cat": "praca",
    "svg": "<rect x=\"3\" y=\"6\" width=\"18\" height=\"12\" rx=\"2\"/><polyline points=\"3.5,7.5 12,13 20.5,7.5\"/>"
  },
  "chart": {
    "label": "Graf",
    "cat": "praca",
    "svg": "<line x1=\"4\" y1=\"20\" x2=\"20\" y2=\"20\"/><rect x=\"6\" y=\"12\" width=\"3\" height=\"8\"/><rect x=\"11\" y=\"8\" width=\"3\" height=\"12\"/><rect x=\"16\" y=\"14\" width=\"3\" height=\"6\"/>"
  },
  "phone": {
    "label": "Telefón",
    "cat": "praca",
    "svg": "<path d=\"M6 3 h3 l1.5 4 -2 1.5 c1 2.5 3 4.5 5.5 5.5 l1.5 -2 4 1.5 v3 a2 2 0 0 1 -2 2 C11 21 4 14 4 6 a2 2 0 0 1 2 -2Z\"/>"
  },
  "flag": {
    "label": "Míľnik",
    "cat": "praca",
    "svg": "<line x1=\"6\" y1=\"3\" x2=\"6\" y2=\"21\"/><path d=\"M6 4 h11 l-2 3 2 3 H6Z\"/>"
  },
  "book": {
    "label": "Kniha",
    "cat": "ucenie",
    "svg": "<path d=\"M6 4 h11 a1 1 0 0 1 1 1 v15 H7 a1 1 0 0 1 -1 -1Z\"/><path d=\"M6 17 h12\"/><line x1=\"9\" y1=\"8\" x2=\"15\" y2=\"8\"/>"
  },
  "cap": {
    "label": "Štúdium",
    "cat": "ucenie",
    "svg": "<path d=\"M2 9 l10 -4 10 4 -10 4Z\"/><path d=\"M6 11 v4 c0 2 12 2 12 0 v-4\"/><line x1=\"22\" y1=\"9\" x2=\"22\" y2=\"14\"/>"
  },
  "pencil": {
    "label": "Ceruzka",
    "cat": "ucenie",
    "svg": "<path d=\"M5 19 l1 -4 9 -9 3 3 -9 9Z\"/><line x1=\"14\" y1=\"7\" x2=\"17\" y2=\"10\"/><path d=\"M5 19 l1 -1.5 1.5 -1\"/>"
  },
  "globe": {
    "label": "Jazyky / svet",
    "cat": "ucenie",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><ellipse cx=\"12\" cy=\"12\" rx=\"3.5\" ry=\"8\"/><line x1=\"4\" y1=\"12\" x2=\"20\" y2=\"12\"/><path d=\"M5 8 h14\"/><path d=\"M5 16 h14\"/>"
  },
  "code": {
    "label": "Kód",
    "cat": "ucenie",
    "svg": "<polyline points=\"9,8 5,12 9,16\"/><polyline points=\"15,8 19,12 15,16\"/>"
  },
  "palette": {
    "label": "Maľovanie",
    "cat": "ucenie",
    "svg": "<path d=\"M12 4 a8 8 0 1 0 0 16 c1.5 0 2 -1 1.5 -2 c-0.5 -1 0 -2 1.5 -2 h1 a4 4 0 0 0 4 -4 c0 -4 -4 -6 -8 -6Z\"/><circle cx=\"8.5\" cy=\"11\" r=\"1\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"12\" cy=\"8.5\" r=\"1\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"15.5\" cy=\"11\" r=\"1\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "camera": {
    "label": "Foto",
    "cat": "ucenie",
    "svg": "<rect x=\"3\" y=\"7\" width=\"18\" height=\"13\" rx=\"2\"/><path d=\"M8 7 l1.5 -2 h5 L16 7\"/><circle cx=\"12\" cy=\"13.5\" r=\"3.5\"/>"
  },
  "puzzle": {
    "label": "Puzzle",
    "cat": "ucenie",
    "svg": "<path d=\"M5 5 h5 a1.5 1.5 0 0 0 3 0 h3 v3 a1.5 1.5 0 0 1 0 3 v3 h-3 a1.5 1.5 0 0 0 -3 0 H5 v-3 a1.5 1.5 0 0 0 0 -3Z\"/>"
  },
  "compass": {
    "label": "Smerovanie",
    "cat": "ucenie",
    "svg": "<circle cx=\"12\" cy=\"12\" r=\"8\"/><polygon points=\"15,9 11,11 9,15 13,13\"/>"
  },
  "mic": {
    "label": "Mikrofón / spev",
    "cat": "ucenie",
    "svg": "<rect x=\"9\" y=\"3\" width=\"6\" height=\"11\" rx=\"3\"/><path d=\"M6 11 a6 6 0 0 0 12 0\"/><line x1=\"12\" y1=\"17\" x2=\"12\" y2=\"20\"/><line x1=\"9\" y1=\"20\" x2=\"15\" y2=\"20\"/>"
  },
  "people": {
    "label": "Priatelia",
    "cat": "socialne",
    "svg": "<circle cx=\"8\" cy=\"8\" r=\"3\"/><circle cx=\"16\" cy=\"8\" r=\"3\"/><path d=\"M3 19 a5 5 0 0 1 10 0\"/><path d=\"M13 19 a5 5 0 0 1 8 -1\"/>"
  },
  "chat": {
    "label": "Správa",
    "cat": "socialne",
    "svg": "<path d=\"M4 6 h16 a1 1 0 0 1 1 1 v8 a1 1 0 0 1 -1 1 H9 l-4 4 v-4 H4 a1 1 0 0 1 -1 -1 V7 a1 1 0 0 1 1 -1Z\"/>"
  },
  "gift": {
    "label": "Darček",
    "cat": "socialne",
    "svg": "<rect x=\"4\" y=\"9\" width=\"16\" height=\"11\" rx=\"1\"/><line x1=\"4\" y1=\"13\" x2=\"20\" y2=\"13\"/><line x1=\"12\" y1=\"9\" x2=\"12\" y2=\"20\"/><path d=\"M12 9 c-1 -4 -5 -4 -5 -1 c0 2 3 1 5 1Z\"/><path d=\"M12 9 c1 -4 5 -4 5 -1 c0 2 -3 1 -5 1Z\"/>"
  },
  "user": {
    "label": "Profil",
    "cat": "socialne",
    "svg": "<circle cx=\"12\" cy=\"8\" r=\"4\"/><path d=\"M5 20 a7 7 0 0 1 14 0\"/>"
  },
  "call": {
    "label": "Hovor",
    "cat": "socialne",
    "svg": "<path d=\"M6 3 h3 l1.5 4 -2 1.5 c1 2.5 3 4.5 5.5 5.5 l1.5 -2 4 1.5 v3 a2 2 0 0 1 -2 2 C11 21 4 14 4 6 a2 2 0 0 1 2 -2Z\"/>"
  },
  "cake": {
    "label": "Oslava",
    "cat": "socialne",
    "svg": "<path d=\"M4 20 v-6 a2 2 0 0 1 2 -2 h12 a2 2 0 0 1 2 2 v6Z\"/><line x1=\"4\" y1=\"16\" x2=\"20\" y2=\"16\"/><line x1=\"8\" y1=\"7\" x2=\"8\" y2=\"10\"/><line x1=\"12\" y1=\"7\" x2=\"12\" y2=\"10\"/><line x1=\"16\" y1=\"7\" x2=\"16\" y2=\"10\"/><circle cx=\"8\" cy=\"6\" r=\"0.7\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"12\" cy=\"6\" r=\"0.7\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"16\" cy=\"6\" r=\"0.7\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "star2": {
    "label": "Obľúbené",
    "cat": "socialne",
    "svg": "<path d=\"M12 3 l2.7 5.5 6 0.9 -4.3 4.2 1 6 -5.4 -2.8 -5.4 2.8 1 -6 -4.3 -4.2 6 -0.9Z\"/>"
  },
  // v29 §7.1 — PLNÁ hviezdička pre štítok náročnosti (star2 je obrysová „obľúbené").
  // cat "ui" ⇒ nechodí do pickera ikon návykov, ale dá sa použiť menom.
  "star": {
    "label": "Hviezdička",
    "cat": "ui",
    "svg": "<path fill=\"currentColor\" stroke-width=\"1\" d=\"M12 3 l2.7 5.5 6 0.9 -4.3 4.2 1 6 -5.4 -2.8 -5.4 2.8 1 -6 -4.3 -4.2 6 -0.9Z\"/>"
  },
  // v29.8 — „coins" (body na míňanie) v tejto sade chýbalo, takže `Tag icon="coins"` v hlavičke
  // sa TICHO nevykreslil (BuzerIcon vracia pri neznámom mene null). cat "ui" ⇒ nejde do pickera
  // ikon návykov. Kresba je tá istá, akú pod menom „coins" doteraz kreslil lucide fallback
  // v icons.tsx — miesta, kde ikona fungovala (Icon name="coins"), vyzerajú presne ako predtým.
  "coins": {
    "label": "Mince",
    "cat": "ui",
    "svg": "<path d=\"M13.744 17.736a6 6 0 1 1-7.48-7.48\"/><path d=\"M15 6h1v4\"/><path d=\"m6.134 14.768.866-.5 2 3.464\"/><circle cx=\"16\" cy=\"8\" r=\"6\"/>"
  },
  "thumbsup": {
    "label": "Páči sa",
    "cat": "socialne",
    "svg": "<path d=\"M7 11 l4 -7 a2 2 0 0 1 2 2 v4 h5 a1.5 1.5 0 0 1 1.5 2 l-1.5 6 a2 2 0 0 1 -2 1.5 H7Z\"/><rect x=\"3\" y=\"11\" width=\"4\" height=\"9\" rx=\"1\"/>"
  },
  "share": {
    "label": "Zdieľať",
    "cat": "socialne",
    "svg": "<circle cx=\"6\" cy=\"12\" r=\"2.5\"/><circle cx=\"17\" cy=\"6\" r=\"2.5\"/><circle cx=\"17\" cy=\"18\" r=\"2.5\"/><line x1=\"8\" y1=\"11\" x2=\"15\" y2=\"7\"/><line x1=\"8\" y1=\"13\" x2=\"15\" y2=\"17\"/>"
  },
  "trophy": {
    "label": "Výhra",
    "cat": "socialne",
    "svg": "<path d=\"M7 4 h10 v4 a5 5 0 0 1 -10 0Z\"/><path d=\"M7 5 H4 v2 a3 3 0 0 0 3 3\"/><path d=\"M17 5 h3 v2 a3 3 0 0 1 -3 3\"/><line x1=\"12\" y1=\"13\" x2=\"12\" y2=\"17\"/><path d=\"M8 20 a4 3 0 0 1 8 0Z\"/>"
  },
  "tree": {
    "label": "Strom",
    "cat": "priroda",
    "svg": "<path d=\"M12 3 c-3 2 -5 5 -5 7 a5 5 0 0 0 10 0 c0 -2 -2 -5 -5 -7Z\"/><line x1=\"12\" y1=\"14\" x2=\"12\" y2=\"21\"/>"
  },
  "leaf": {
    "label": "List",
    "cat": "priroda",
    "svg": "<path d=\"M5 19 C5 9 12 5 19 5 C19 15 12 19 5 19Z\"/><path d=\"M5 19 C9 15 13 11 17 8\"/>"
  },
  "flower": {
    "label": "Kvet",
    "cat": "priroda",
    "svg": "<ellipse cx=\"12.00\" cy=\"6.70\" rx=\"1.7\" ry=\"3.0\" transform=\"rotate(0.0 12.00 6.70)\"/><ellipse cx=\"15.14\" cy=\"8.98\" rx=\"1.7\" ry=\"3.0\" transform=\"rotate(72.0 15.14 8.98)\"/><ellipse cx=\"13.94\" cy=\"12.67\" rx=\"1.7\" ry=\"3.0\" transform=\"rotate(144.0 13.94 12.67)\"/><ellipse cx=\"10.06\" cy=\"12.67\" rx=\"1.7\" ry=\"3.0\" transform=\"rotate(216.0 10.06 12.67)\"/><ellipse cx=\"8.86\" cy=\"8.98\" rx=\"1.7\" ry=\"3.0\" transform=\"rotate(288.0 8.86 8.98)\"/><circle cx=\"12\" cy=\"10\" r=\"1.9\"/><line x1=\"12\" y1=\"13\" x2=\"12\" y2=\"21\"/><path d=\"M12 17 c2.5 0 3.5 -1.5 3.5 -3 c-2.5 0 -3.5 1.5 -3.5 3Z\"/>"
  },
  "cloud": {
    "label": "Oblak",
    "cat": "priroda",
    "svg": "<path d=\"M7 18 a4 4 0 0 1 0 -8 a5 5 0 0 1 9.5 1 a3.5 3.5 0 0 1 -1.5 7Z\"/>"
  },
  // v31 — rastové štádiá pre dlaždicu „splnené dnes" (semienko → strom); indexované grow0..grow4
  "grow0": {
    "label": "Semienko", "cat": "ui",
    "svg": "<path d=\"M4 20 h16\"/><path d=\"M12 11 c2.6 1.8 2.6 6.2 0 8 c-2.6 -1.8 -2.6 -6.2 0 -8Z\"/>"
  },
  "growseed": {
    "label": "Zasadené semienko", "cat": "ui",
    "svg": "<path d=\"M4 20 h16\"/><path d=\"M7.5 20 q4.5 -5 9 0\"/><ellipse cx=\"12\" cy=\"17.6\" rx=\"1.5\" ry=\"2\"/>"
  },
  "grow1": {
    "label": "Klíček", "cat": "ui",
    "svg": "<path d=\"M4 20 h16\"/><path d=\"M12 20 v-5.5\"/><path d=\"M12 16 c-1.8 -0.2 -3.2 -1.3 -3.6 -3.2 c2.2 0.2 3.2 1.3 3.6 3.2Z\"/><path d=\"M12 15 c1.8 -0.2 3.2 -1.3 3.6 -3.2 c-2.2 0.2 -3.2 1.3 -3.6 3.2Z\"/>"
  },
  "grow2": {
    "label": "Sadenica", "cat": "ui",
    "svg": "<path d=\"M4 20 h16\"/><path d=\"M12 20 v-9\"/><path d=\"M12 16.5 c-2.3 -0.2 -3.9 -1.5 -4.4 -3.8 c2.8 0.2 4 1.5 4.4 3.8Z\"/><path d=\"M12 14 c2.3 -0.2 3.9 -1.5 4.4 -3.8 c-2.8 0.2 -4 1.5 -4.4 3.8Z\"/>"
  },
  "grow3": {
    "label": "Rastlinka", "cat": "ui",
    "svg": "<path d=\"M4 20 h16\"/><path d=\"M12 20 v-11\"/><path d=\"M12 17 c-2.7 -0.2 -4.4 -1.6 -5 -4.2 c3.2 0.2 4.5 1.6 5 4.2Z\"/><path d=\"M12 14 c2.7 -0.2 4.4 -1.6 5 -4.2 c-3.2 0.2 -4.5 1.6 -5 4.2Z\"/><circle cx=\"12\" cy=\"7.5\" r=\"2.2\"/>"
  },
  "grow4": {
    "label": "Strom", "cat": "ui",
    "svg": "<path d=\"M4 20 h16\"/><path d=\"M12 20 v-6\"/><path d=\"M12 3 c-4 2.5 -6 5.5 -6 8 a6 6 0 0 0 12 0 c0 -2.5 -2 -5.5 -6 -8Z\"/>"
  },
  "rain": {
    "label": "Dážď",
    "cat": "priroda",
    "svg": "<path d=\"M7 13 a4 4 0 0 1 0 -8 a5 5 0 0 1 9.5 1 a3.5 3.5 0 0 1 -1.5 7Z\"/><line x1=\"8\" y1=\"16\" x2=\"7\" y2=\"19\"/><line x1=\"12\" y1=\"16\" x2=\"11\" y2=\"19\"/><line x1=\"16\" y1=\"16\" x2=\"15\" y2=\"19\"/>"
  },
  "snow": {
    "label": "Sneh",
    "cat": "priroda",
    "svg": "<line x1=\"12\" y1=\"12\" x2=\"12.00\" y2=\"4.00\"/><line x1=\"12.00\" y1=\"7.60\" x2=\"13.48\" y2=\"5.71\"/><line x1=\"12.00\" y1=\"7.60\" x2=\"10.52\" y2=\"5.71\"/><line x1=\"12\" y1=\"12\" x2=\"18.93\" y2=\"8.00\"/><line x1=\"15.81\" y1=\"9.80\" x2=\"18.19\" y2=\"10.13\"/><line x1=\"15.81\" y1=\"9.80\" x2=\"16.71\" y2=\"7.57\"/><line x1=\"12\" y1=\"12\" x2=\"18.93\" y2=\"16.00\"/><line x1=\"15.81\" y1=\"14.20\" x2=\"16.71\" y2=\"16.43\"/><line x1=\"15.81\" y1=\"14.20\" x2=\"18.19\" y2=\"13.87\"/><line x1=\"12\" y1=\"12\" x2=\"12.00\" y2=\"20.00\"/><line x1=\"12.00\" y1=\"16.40\" x2=\"10.52\" y2=\"18.29\"/><line x1=\"12.00\" y1=\"16.40\" x2=\"13.48\" y2=\"18.29\"/><line x1=\"12\" y1=\"12\" x2=\"5.07\" y2=\"16.00\"/><line x1=\"8.19\" y1=\"14.20\" x2=\"5.81\" y2=\"13.87\"/><line x1=\"8.19\" y1=\"14.20\" x2=\"7.29\" y2=\"16.43\"/><line x1=\"12\" y1=\"12\" x2=\"5.07\" y2=\"8.00\"/><line x1=\"8.19\" y1=\"9.80\" x2=\"7.29\" y2=\"7.57\"/><line x1=\"8.19\" y1=\"9.80\" x2=\"5.81\" y2=\"10.13\"/>"
  },
  "paw": {
    "label": "Maznáčik",
    "cat": "priroda",
    "svg": "<path d=\"M12 13.5 c-2.6 0 -4.5 1.8 -4.5 3.8 c0 1.6 1.4 2.4 2.7 2.4 c0.7 0 1.2 -0.4 1.8 -0.4 c0.6 0 1.1 0.4 1.8 0.4 c1.3 0 2.7 -0.8 2.7 -2.4 c0 -2 -1.9 -3.8 -4.5 -3.8Z\"/><ellipse cx=\"6.8\" cy=\"11.5\" rx=\"1.5\" ry=\"2\"/><ellipse cx=\"10.2\" cy=\"8.8\" rx=\"1.6\" ry=\"2.1\"/><ellipse cx=\"13.8\" cy=\"8.8\" rx=\"1.6\" ry=\"2.1\"/><ellipse cx=\"17.2\" cy=\"11.5\" rx=\"1.5\" ry=\"2\"/>"
  },
  "bird": {
    "label": "Vták",
    "cat": "priroda",
    "svg": "<path d=\"M4 9 c4 0 6 3 8 3 c2 0 3 -2 6 -2 l3 -2 -1 4 c-1 4 -5 7 -9 7 c-4 0 -7 -3 -7 -7Z\"/><circle cx=\"16\" cy=\"9.5\" r=\"0.7\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "wave": {
    "label": "More",
    "cat": "priroda",
    "svg": "<path d=\"M3 9 q2 -2 4.5 0 t4.5 0 4.5 0 4.5 0\"/><path d=\"M3 13 q2 -2 4.5 0 t4.5 0 4.5 0 4.5 0\"/><path d=\"M3 17 q2 -2 4.5 0 t4.5 0 4.5 0 4.5 0\"/>"
  },
  "wind": {
    "label": "Vietor",
    "cat": "priroda",
    "svg": "<path d=\"M3 9 h11 a2.5 2.5 0 1 0 -2.5 -2.5\"/><path d=\"M3 14 h15 a2.5 2.5 0 1 1 -2.5 2.5\"/><path d=\"M3 19 h8 a2 2 0 1 1 -2 2\"/>"
  },
  "check": {
    "label": "Hotovo / vybrané",
    "cat": "ui",
    "svg": "<polyline points=\"5,12.5 10,17.5 19,7\"/>"
  },
  "close": {
    "label": "Zavrieť",
    "cat": "ui",
    "svg": "<line x1=\"6\" y1=\"6\" x2=\"18\" y2=\"18\"/><line x1=\"18\" y1=\"6\" x2=\"6\" y2=\"18\"/>"
  },
  "dots": {
    "label": "Viac",
    "cat": "ui",
    "svg": "<circle cx=\"12\" cy=\"5\" r=\"1.8\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"12\" cy=\"12\" r=\"1.8\" fill=\"currentColor\" stroke=\"none\"/><circle cx=\"12\" cy=\"19\" r=\"1.8\" fill=\"currentColor\" stroke=\"none\"/>"
  },
  "add": {
    "label": "Pridať",
    "cat": "ui",
    "svg": "<line x1=\"12\" y1=\"5\" x2=\"12\" y2=\"19\"/><line x1=\"5\" y1=\"12\" x2=\"19\" y2=\"12\"/>"
  },
  "search": {
    "label": "Hľadať",
    "cat": "ui",
    "svg": "<circle cx=\"11\" cy=\"11\" r=\"6.5\"/><line x1=\"15.8\" y1=\"15.8\" x2=\"20\" y2=\"20\"/>"
  },
  "filter": {
    "label": "Filter",
    "cat": "ui",
    "svg": "<polygon points=\"21,4 3,4 10,12.4 10,19 14,21 14,12.4 21,4\"/>"
  },
  "bell": {
    "label": "Notifikácie",
    "cat": "ui",
    "svg": "<path d=\"M18 16 v-5 a6 6 0 0 0 -12 0 v5 l-1.5 2 h15Z\"/><path d=\"M10 18 a2 2 0 0 0 4 0\"/><line x1=\"12\" y1=\"3\" x2=\"12\" y2=\"5\"/>"
  },
  "belloff": {
    "label": "Stíšené",
    "cat": "ui",
    "svg": "<path d=\"M18 16 v-5 a6 6 0 0 0 -12 0 v5 l-1.5 2 h15Z\"/><path d=\"M10 18 a2 2 0 0 0 4 0\"/><line x1=\"12\" y1=\"3\" x2=\"12\" y2=\"5\"/><line x1=\"4\" y1=\"20\" x2=\"20\" y2=\"4\"/>"
  },
  "chevron": {
    "label": "Ďalej",
    "cat": "ui",
    "svg": "<polyline points=\"9,6 15,12 9,18\"/>"
  },
  "back": {
    "label": "Späť",
    "cat": "ui",
    "svg": "<polyline points=\"15,6 9,12 15,18\"/>"
  },
  "logout": {
    "label": "Odhlásiť",
    "cat": "ui",
    "svg": "<path d=\"M14 4 H6 a1 1 0 0 0 -1 1 v14 a1 1 0 0 0 1 1 h8\"/><polyline points=\"14,8 18,12 14,16\"/><line x1=\"18\" y1=\"12\" x2=\"9\" y2=\"12\"/>"
  },
  "refresh": {
    "label": "Obnoviť",
    "cat": "ui",
    "svg": "<path d=\"M20 12 a8 8 0 1 1 -2.3 -5.7\"/><polyline points=\"20,3 20,8 15,8\"/>"
  },
  "undo": {
    "label": "Vrátiť späť",
    "cat": "ui",
    "svg": "<polyline points=\"9,7 5,11 9,15\"/><path d=\"M5 11 H14 a5 5 0 0 1 0 10 H10\"/>"
  },
  "settings": {
    "label": "Nastavenia",
    "cat": "ui",
    "svg": "<path d=\"M12.00 2.80 L14.60 5.72 L18.51 5.49 L18.28 9.40 L21.20 12.00 L18.28 14.60 L18.51 18.51 L14.60 18.28 L12.00 21.20 L9.40 18.28 L5.49 18.51 L5.72 14.60 L2.80 12.00 L5.72 9.40 L5.49 5.49 L9.40 5.72 Z\"/><circle cx=\"12\" cy=\"12\" r=\"3.1\"/>"
  },
  "shop": {
    "label": "Obchod",
    "cat": "ui",
    "svg": "<path d=\"M5 8 h14 l-1 11 a1 1 0 0 1 -1 1 H7 a1 1 0 0 1 -1 -1Z\"/><path d=\"M9 8 V6.5 a3 3 0 0 1 6 0 V8\"/>"
  },
  "crown": {
    "label": "Premium",
    "cat": "ui",
    "svg": "<path d=\"M5.5 18 L4 8.5 L8.5 12.5 L12 6 L15.5 12.5 L20 8.5 L18.5 18 Z\"/><line x1=\"5.5\" y1=\"18\" x2=\"18.5\" y2=\"18\"/>"
  },
  "send": {
    "label": "Odoslať",
    "cat": "ui",
    "svg": "<path d=\"M21 3 L3 10.5 L10 13 L13 20 Z\"/><line x1=\"10\" y1=\"13\" x2=\"21\" y2=\"3\"/>"
  },
  "megaphone": {
    "label": "Oznam / nahlásiť",
    "cat": "ui",
    "svg": "<path d=\"M4 10 h3 l9 -5 v14 l-9 -5 H4 a1 1 0 0 1 -1 -1 v-2 a1 1 0 0 1 1 -1Z\"/><path d=\"M7 15 v3 a1 1 0 0 0 1 1 h1.5 a1 1 0 0 0 1 -1 v-2.5\"/><path d=\"M19 9.5 a4 4 0 0 1 0 5\"/>"
  },
  "addfriend": {
    "label": "Pozvať priateľa",
    "cat": "ui",
    "svg": "<circle cx=\"9\" cy=\"8\" r=\"3.5\"/><path d=\"M3 19 a6 6 0 0 1 12 0\"/><line x1=\"18\" y1=\"7\" x2=\"18\" y2=\"13\"/><line x1=\"15\" y1=\"10\" x2=\"21\" y2=\"10\"/>"
  },
  "buzerak": {
    "label": "Šťuchnúť",
    "cat": "ui",
    "svg": "<rect x=\"6\" y=\"9\" width=\"9\" height=\"10\" rx=\"3\"/><path d=\"M15 10.5 h6 a1.5 1.5 0 0 1 0 3 h-6Z\"/><path d=\"M8.5 9 V7.5 a1.5 1.5 0 0 1 3 0 V9\"/><path d=\"M9 14 h4\"/><path d=\"M9 16.5 h3\"/>"
  },
  // v20 §26 — Bez mobilu (digitálny detox)
  "nophone": {
    "label": "Bez mobilu",
    "cat": "mysel",
    "svg": "<rect x=\"7\" y=\"3\" width=\"10\" height=\"18\" rx=\"2.5\"/><line x1=\"10.5\" y1=\"18.3\" x2=\"13.5\" y2=\"18.3\"/><line x1=\"4\" y1=\"4.5\" x2=\"20\" y2=\"19.5\"/>"
  },
  // v20 — Zámok (zamknuté levely)
  "lock": {
    "label": "Zámok",
    "cat": "ui",
    "svg": "<rect x=\"5\" y=\"11\" width=\"14\" height=\"9\" rx=\"2\"/><path d=\"M8 11 V7.5 a4 4 0 0 1 8 0 V11\"/><circle cx=\"12\" cy=\"14.7\" r=\"1.2\"/><line x1=\"12\" y1=\"15.3\" x2=\"12\" y2=\"17.6\"/>"
  },
  // v20 §27 — Raketa (onboarding „Začať")
  "rocket": {
    "label": "Raketa",
    "cat": "praca",
    "svg": "<g transform=\"rotate(45 12 12)\"><path d=\"M12 2.5 C14 5 15 8.5 15 11.5 C15 13.6 14.3 15.3 13.3 16 H10.7 C9.7 15.3 9 13.6 9 11.5 C9 8.5 10 5 12 2.5Z\"/><circle cx=\"12\" cy=\"8.5\" r=\"1.7\"/><path d=\"M9 12.5 L6 15.5 L9 15.5\"/><path d=\"M15 12.5 L18 15.5 L15 15.5\"/><line x1=\"10.5\" y1=\"17.5\" x2=\"10\" y2=\"20\"/><line x1=\"12\" y1=\"17.5\" x2=\"12\" y2=\"21\"/><line x1=\"13.5\" y1=\"17.5\" x2=\"14\" y2=\"20\"/></g>"
  }
};
// v20 §23/§25 — merge new badge + level icons into the registry (defined separately for clarity)
import { NEW_BADGE_ICONS } from "./BadgeIcons";
import { NEW_LEVEL_ICONS } from "./LevelIcons";
import { NEW_SHOP_ICONS } from "./ShopIcons";
Object.assign(ICONS, NEW_BADGE_ICONS, NEW_LEVEL_ICONS, NEW_SHOP_ICONS);

/** v29.8 — pojmové premenovania: mená, ktorými appka ikonu volá, ale v tejto sade sa volá inak.
 *  Dovtedy `BuzerIcon` pri takom mene vrátil `null`, takže ikona sa TICHO nevykreslila (štítok
 *  „Oficiálna", nadpisy „Naozaj zmazať?", počet priateľov v profile, hlavička výzvy, uvítanie
 *  pri registrácii). Zrkadlí `BUZER_MAP` v `icons.tsx` — ten istý preklad musí platiť, či ikonu
 *  kreslí wrapper `Icon`, alebo priamo `Tag`/`Modal`/`SectionHeader` cez `BuzerIcon`. */
const ALIASES: Record<string, string> = {
  points: "star2", delete: "trash", badge: "trophy", friends: "people",
  challenge: "flag", sprout: "growseed",
};

export function BuzerIcon({ name, size = 24, strokeWidth = 2, color = "currentColor", className, title, style }: Props) {
  const def = ICONS[name] || ICONS[ALIASES[name] as never];
  if (!def) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      /* `color` musí ísť na element, nielen do `stroke`: viacero ikon má plné detaily
         (oči sovy, bodky, zrnká) cez fill="currentColor" — tie berú farbu z CSS `color`,
         takže bez tohto ostali v zdedenej (čiernej) farbe, kým zvyšok ikony bol biely. */
      style={{ flexShrink: 0, color, ...(style || {}) }}
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      dangerouslySetInnerHTML={{ __html: (title ? `<title>${title}</title>` : "") + def.svg }}
    />
  );
}

export const ICON_NAMES = Object.keys(ICONS) as BuzerIconName[];

export const ICON_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(ICONS).map(([k, v]) => [k, v.label])
);

export const ICON_CATEGORIES: Record<string, BuzerIconName[]> = Object.keys(CATEGORY_LABELS).reduce(
  (acc, cat) => {
    acc[cat] = (Object.entries(ICONS)
      .filter(([, v]) => v.cat === cat)
      .map(([k]) => k)) as BuzerIconName[];
    return acc;
  },
  {} as Record<string, BuzerIconName[]>
);

/** Obsahové kategórie (bez UI chrome) — toto použi v pickeri ikon pre rutiny/návyky. */
export const CONTENT_CATEGORY_KEYS = Object.keys(CATEGORY_LABELS).filter((c) => c !== "ui");

/** UI-chrome ikony (check, close, bell, send, …) — nepatria do pickeru kategórií. */
export const UI_ICON_NAMES = (ICON_CATEGORIES["ui"] ?? []) as BuzerIconName[];
